const TRANSFORMERS_MODULE_URL = "https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.8.1/+esm";
const DEFAULT_MODEL = "onnx-community/whisper-tiny";
const TARGET_SAMPLE_RATE = 16000;
const SILENCE_MS = 850;
const MAX_UTTERANCE_MS = 14000;
const MIN_UTTERANCE_MS = 280;
const SPEECH_RMS = 0.012;
const PRE_ROLL_MS = 360;
const MAX_CAPTURE_BUFFER_MS = MAX_UTTERANCE_MS + SILENCE_MS + PRE_ROLL_MS + 500;
const WORKER_WARMUP_TIMEOUT_MS = 90000;
const WORKER_TRANSCRIBE_TIMEOUT_MS = 60000;
const ONE_SHOT_NO_SPEECH_TIMEOUT_MS = 10000;
const AUDIO_FLOW_WATCHDOG_INTERVAL_MS = 1000;
const AUDIO_FLOW_STALL_TIMEOUT_MS = 7000;
const AUDIO_CONTEXT_RECOVERY_GRACE_MS = 2500;

let sharedWorker = null;
let workerRequestId = 0;
const pendingWorkerRequests = new Map();

function nativeSpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function isLikelyMobileRuntime() {
  if (typeof navigator === "undefined") return false;
  if (navigator.userAgentData?.mobile === true) return true;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(String(navigator.userAgent || ""));
}

function canUseLocalSttFallback() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (typeof Worker === "undefined" || !AudioContextCtor || !navigator.mediaDevices?.getUserMedia) return false;

  // Whisper/Transformers in-browser is a useful desktop fallback, but it can
  // consume enough WASM/WebGPU memory to freeze or kill a mobile tab. Never
  // use the heavy fallback on mobile/low-memory devices; native browser STT
  // remains the primary path there.
  const deviceMemory = Number(navigator.deviceMemory || 0);
  const hardwareConcurrency = Number(navigator.hardwareConcurrency || 0);
  if (isLikelyMobileRuntime()) return false;
  // Unknown capacity is not enough evidence to start a multi-hundred-MB model.
  // Prefer disabling voice on that browser over risking a renderer crash.
  if (!deviceMemory || deviceMemory < 8) return false;
  if (hardwareConcurrency > 0 && hardwareConcurrency < 4) return false;
  return true;
}

function normalizedNativeError(event) {
  const code = String(event?.error || event?.name || "").trim();
  if (code === "not-allowed" || code === "service-not-allowed") return "not-allowed";
  if (code === "audio-capture") return "audio-capture";
  if (code === "no-speech") return "no-speech";
  if (code === "aborted") return "aborted";
  return code || "speech-recognition";
}

function workerFailure(message, code = "local-stt-worker") {
  const error = new Error(message || "Falló el worker local de reconocimiento de voz.");
  error.code = code;
  return error;
}

function localCaptureFailure(message, code = "local-stt-audio-capture") {
  const error = new Error(message || "Se interrumpió la captura local de audio.");
  error.name = "NotReadableError";
  error.code = code;
  return error;
}

function rejectWorkerRequests(worker, error) {
  for (const [requestId, pending] of pendingWorkerRequests.entries()) {
    if (pending.worker !== worker) continue;
    pendingWorkerRequests.delete(requestId);
    if (pending.timer) clearTimeout(pending.timer);
    try { pending.reject(error); } catch { /* promise observer only */ }
  }
}

function failWorker(worker, error) {
  rejectWorkerRequests(worker, error);
  try { worker?.terminate?.(); } catch { /* noop */ }
  if (sharedWorker === worker) sharedWorker = null;
}

function createWorker() {
  const source = `
    import { pipeline, env } from ${JSON.stringify(TRANSFORMERS_MODULE_URL)};

    env.allowLocalModels = false;
    env.useBrowserCache = true;

    let transcriberPromise = null;
    let activeDevice = null;
    let transcriptionQueue = Promise.resolve();

    async function getTranscriber() {
      if (transcriberPromise) return transcriberPromise;
      activeDevice = self.navigator && self.navigator.gpu ? "webgpu" : "wasm";
      const loading = pipeline(
        "automatic-speech-recognition",
        ${JSON.stringify(DEFAULT_MODEL)},
        {
          device: activeDevice,
          dtype: activeDevice === "webgpu" ? "fp16" : "q8",
          progress_callback: (progress) => {
            self.postMessage({ type: "model-progress", progress });
          },
        },
      ).catch(async (error) => {
        if (activeDevice !== "webgpu") throw error;
        activeDevice = "wasm";
        return pipeline(
          "automatic-speech-recognition",
          ${JSON.stringify(DEFAULT_MODEL)},
          { device: "wasm", dtype: "q8" },
        );
      });
      transcriberPromise = loading.catch((error) => {
        transcriberPromise = null;
        throw error;
      });
      return transcriberPromise;
    }

    self.onmessage = async (event) => {
      const message = event.data || {};
      if (message.type === "warmup") {
        try {
          await getTranscriber();
          self.postMessage({ type: "ready", requestId: message.requestId, device: activeDevice });
        } catch (error) {
          self.postMessage({ type: "error", requestId: message.requestId, error: String(error?.message || error) });
        }
        return;
      }
      if (message.type !== "transcribe") return;
      const run = async () => {
        try {
          const transcriber = await getTranscriber();
          const audio = new Float32Array(message.audio);
          const output = await transcriber(audio, {
            language: "spanish",
            task: "transcribe",
            return_timestamps: false,
          });
          self.postMessage({
            type: "result",
            requestId: message.requestId,
            text: String(output?.text || "").trim(),
            device: activeDevice,
          });
        } catch (error) {
          self.postMessage({ type: "error", requestId: message.requestId, error: String(error?.message || error) });
        }
      };
      // ONNX/WebGPU can spike memory badly if a stale utterance and a new one
      // reach the same worker together. Keep inference strictly serialized.
      transcriptionQueue = transcriptionQueue.catch(() => undefined).then(run);
    };
  `;
  const blob = new Blob([source], { type: "text/javascript" });
  const url = URL.createObjectURL(blob);
  const worker = new Worker(url, { type: "module", name: "owner-whisper-stt" });
  URL.revokeObjectURL(url);
  worker.onmessage = (event) => {
    const message = event.data || {};
    if (message.type === "model-progress") {
      window.dispatchEvent(new CustomEvent("wp-owner-stt-progress", { detail: message.progress || {} }));
      return;
    }
    const pending = pendingWorkerRequests.get(message.requestId);
    if (!pending || pending.worker !== worker) return;
    pendingWorkerRequests.delete(message.requestId);
    if (pending.timer) clearTimeout(pending.timer);
    if (message.type === "error") {
      const error = workerFailure(message.error || "No pude cargar el motor local de voz.", "local-stt-worker-error");
      pending.reject(error);
    } else {
      pending.resolve(message);
    }
  };
  worker.onerror = (event) => {
    failWorker(worker, workerFailure(event?.message || "Falló el worker local de reconocimiento de voz.", "local-stt-worker-error"));
  };
  worker.onmessageerror = () => {
    failWorker(worker, workerFailure("El worker local devolvió una respuesta inválida.", "local-stt-worker-message"));
  };
  return worker;
}

function getWorker() {
  if (!sharedWorker) sharedWorker = createWorker();
  return sharedWorker;
}

function workerRequest(type, payload = {}, transfer = []) {
  const worker = getWorker();
  const requestId = ++workerRequestId;
  const timeoutMs = type === "warmup" ? WORKER_WARMUP_TIMEOUT_MS : WORKER_TRANSCRIBE_TIMEOUT_MS;
  return new Promise((resolve, reject) => {
    const pending = { resolve, reject, worker, timer: null };
    pending.timer = setTimeout(() => {
      if (pendingWorkerRequests.get(requestId) !== pending) return;
      failWorker(
        worker,
        workerFailure(
          type === "warmup"
            ? "El motor local de voz tardó demasiado en cargar."
            : "El motor local de voz tardó demasiado en transcribir.",
          "local-stt-worker-timeout",
        ),
      );
    }, timeoutMs);
    pendingWorkerRequests.set(requestId, pending);
    try {
      worker.postMessage({ type, requestId, ...payload }, transfer);
    } catch (error) {
      failWorker(
        worker,
        workerFailure(
          String(error?.message || error || "No pude enviar audio al worker local de voz."),
          "local-stt-worker-post-message",
        ),
      );
    }
  });
}

export function isOwnerLocalSttSupported() {
  return Boolean(nativeSpeechRecognitionCtor()) || canUseLocalSttFallback();
}

export async function warmOwnerLocalStt() {
  if (nativeSpeechRecognitionCtor()) return { device: "browser-native" };
  if (!canUseLocalSttFallback()) throw new Error("Este navegador no ofrece un motor de voz seguro para este dispositivo.");
  return workerRequest("warmup");
}

function mixToMono(audioBuffer) {
  const length = audioBuffer.length;
  const channels = Math.max(1, audioBuffer.numberOfChannels || 1);
  const mono = new Float32Array(length);
  for (let channel = 0; channel < channels; channel += 1) {
    const data = audioBuffer.getChannelData(channel);
    for (let index = 0; index < length; index += 1) mono[index] += data[index] / channels;
  }
  return mono;
}

function resampleLinear(input, sourceRate, targetRate = TARGET_SAMPLE_RATE) {
  if (sourceRate === targetRate) return input;
  const ratio = sourceRate / targetRate;
  const length = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(length);
  for (let index = 0; index < length; index += 1) {
    const position = index * ratio;
    const left = Math.floor(position);
    const right = Math.min(input.length - 1, left + 1);
    const mix = position - left;
    output[index] = input[left] * (1 - mix) + input[right] * mix;
  }
  return output;
}

function normalizedError(error) {
  const name = String(error?.name || "");
  if (name === "NotAllowedError" || name === "SecurityError") return "not-allowed";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "audio-capture";
  if (name === "NotReadableError" || name === "TrackStartError") return "audio-capture";
  return "local-stt";
}

function makeResultEvent(text) {
  const alternative = { transcript: text, confidence: 1 };
  const row = [alternative];
  row.isFinal = true;
  return { resultIndex: 0, results: [row] };
}

export class OwnerLocalSpeechRecognition {
  constructor() {
    this.lang = "es-AR";
    this.continuous = false;
    this.interimResults = false;
    this.maxAlternatives = 1;
    this.onstart = null;
    this.onend = null;
    this.onerror = null;
    this.onresult = null;
    this.onstatechange = null;
    this.state = "idle";
    this._stream = null;
    this._audioContext = null;
    this._source = null;
    this._processor = null;
    this._chunks = [];
    this._bufferedSamples = 0;
    this._startedAt = 0;
    this._speechStartedAt = 0;
    this._lastSpeechAt = 0;
    this._heardSpeech = false;
    this._stopping = false;
    this._aborted = false;
    this._processing = false;
    this._generation = 0;
    this._startPromise = null;
    this._finishPromise = null;
    this._nativeRecognition = null;
    this._captureWatchdogTimer = null;
    this._noSpeechTimer = null;
    this._trackEndHandlers = [];
    this._lastAudioFrameAt = 0;
    this._audioResumeAttemptedAt = 0;
    this._audioStallDetectedAt = 0;
    this._captureFailurePromise = null;
  }

  _transition(nextState, { operation = "" } = {}) {
    const previousState = this.state;
    if (previousState === nextState) return { previousState, state: nextState, operation, changed: false };
    this.state = nextState;
    const detail = { previousState, state: nextState, operation, changed: true };
    try { this.onstatechange?.(detail); } catch { /* observer only */ }
    return detail;
  }

  _failureLifecycle({ operation, previousState, attemptedState } = {}) {
    return {
      operation: String(operation || "recognition"),
      previousState: String(previousState || this.state || "unknown"),
      attemptedState: String(attemptedState || "unknown"),
      recognitionState: String(this.state || "unknown"),
    };
  }

  _startNative(NativeRecognition) {
    if (this._nativeRecognition) return undefined;
    if (["starting", "listening", "stopping", "cancelling", "processing"].includes(this.state)) return undefined;

    const generation = this._generation + 1;
    this._generation = generation;
    this._aborted = false;
    this._stopping = false;
    this._transition("starting", { operation: "start" });

    let recognition = null;
    try {
      recognition = new NativeRecognition();
      this._nativeRecognition = recognition;
      recognition.lang = this.lang || "es-AR";
      recognition.continuous = Boolean(this.continuous);
      recognition.interimResults = Boolean(this.interimResults);
      recognition.maxAlternatives = Math.max(1, Number(this.maxAlternatives || 1));

      const isCurrent = () => generation === this._generation && this._nativeRecognition === recognition;

      recognition.onstart = () => {
        if (!isCurrent()) return;
        this._transition("listening", { operation: "start" });
        try { this.onstart?.(); } catch { /* consumer observer only */ }
      };
      recognition.onresult = (event) => {
        if (!isCurrent() || this._aborted) return;
        try { this.onresult?.(event); } catch { /* consumer observer only */ }
      };
      recognition.onerror = (event) => {
        if (!isCurrent()) return;
        const code = normalizedNativeError(event);
        const previousState = this.state;
        this._nativeRecognition = null;
        this._stopping = false;
        if (code === "aborted" || code === "no-speech") this._transition("idle", { operation: "recognition" });
        else this._transition("error", { operation: "recognition" });
        try {
          this.onerror?.({
            error: code,
            detail: event,
            lifecycle: this._failureLifecycle({ operation: "recognition", previousState, attemptedState: "idle" }),
          });
        } catch { /* consumer observer only */ }
      };
      recognition.onend = () => {
        if (!isCurrent()) return;
        const previousState = this.state;
        this._nativeRecognition = null;
        this._stopping = false;
        if (this.state !== "error") this._transition("idle", { operation: "end" });
        try { this.onend?.({ previousState }); } catch { /* consumer observer only */ }
      };

      recognition.start();
      return undefined;
    } catch (error) {
      if (recognition) {
        try { recognition.abort?.(); } catch { /* noop */ }
      }
      if (generation !== this._generation) return undefined;
      this._nativeRecognition = null;
      const previousState = this.state;
      this._transition("error", { operation: "start" });
      try {
        this.onerror?.({
          error: normalizedNativeError(error),
          detail: error,
          lifecycle: this._failureLifecycle({ operation: "start", previousState, attemptedState: "listening" }),
        });
      } catch { /* consumer observer only */ }
      try { this.onend?.(); } catch { /* consumer observer only */ }
      return undefined;
    }
  }

  async start() {
    const NativeRecognition = nativeSpeechRecognitionCtor();
    if (NativeRecognition) return this._startNative(NativeRecognition);
    if (!canUseLocalSttFallback()) {
      const error = new Error("No hay un motor de reconocimiento de voz seguro disponible en este dispositivo.");
      error.name = "NotSupportedError";
      throw error;
    }
    if (this._startPromise) return this._startPromise;
    if (["starting", "listening", "stopping", "cancelling", "processing"].includes(this.state)) return undefined;

    const generation = this._generation + 1;
    this._generation = generation;
    this._aborted = false;
    this._stopping = false;
    this._transition("starting", { operation: "start" });

    const pending = (async () => {
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
        if (generation !== this._generation || this.state !== "starting") {
          stream?.getTracks?.().forEach((track) => { try { track.stop(); } catch { /* noop */ } });
          return;
        }
        this._stream = stream;
        stream = null;
        const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
        this._audioContext = new AudioContextCtor({ latencyHint: "interactive" });
        await this._audioContext.resume();
        if (generation !== this._generation || this.state !== "starting") {
          await this._cleanup();
          return;
        }
        this._source = this._audioContext.createMediaStreamSource(this._stream);
        this._processor = this._audioContext.createScriptProcessor(4096, 1, 1);
        this._chunks = [];
        this._bufferedSamples = 0;
        this._startedAt = performance.now();
        this._speechStartedAt = 0;
        this._lastSpeechAt = 0;
        this._heardSpeech = false;
        this._processor.onaudioprocess = (event) => this._onAudio(event);
        this._source.connect(this._processor);
        this._processor.connect(this._audioContext.destination);
        this._transition("listening", { operation: "start" });
        this._armCaptureGuards(generation);
        this.onstart?.();
        warmOwnerLocalStt().catch(() => undefined);
      } catch (error) {
        if (stream) stream.getTracks?.().forEach((track) => { try { track.stop(); } catch { /* noop */ } });
        if (generation !== this._generation) return;
        const previousState = this.state;
        await this._cleanup();
        this._transition("error", { operation: "start" });
        this.onerror?.({
          error: normalizedError(error),
          detail: error,
          lifecycle: this._failureLifecycle({ operation: "start", previousState, attemptedState: "listening" }),
        });
        this.onend?.();
      }
    })();

    this._startPromise = pending;
    try { return await pending; } finally {
      if (this._startPromise === pending) this._startPromise = null;
    }
  }

  _trimBufferedAudio(maxMs, sampleRate) {
    const maxSamples = Math.max(1, Math.ceil((Number(sampleRate) || 48000) * maxMs / 1000));
    while (this._bufferedSamples > maxSamples && this._chunks.length > 1) {
      const dropped = this._chunks.shift();
      this._bufferedSamples -= dropped?.length || 0;
    }
  }

  _clearCaptureGuards() {
    if (this._captureWatchdogTimer) {
      clearInterval(this._captureWatchdogTimer);
      this._captureWatchdogTimer = null;
    }
    if (this._noSpeechTimer) {
      clearTimeout(this._noSpeechTimer);
      this._noSpeechTimer = null;
    }
    for (const row of this._trackEndHandlers) {
      try { row.track?.removeEventListener?.("ended", row.handler); } catch { /* noop */ }
    }
    this._trackEndHandlers = [];
    this._lastAudioFrameAt = 0;
    this._audioResumeAttemptedAt = 0;
    this._audioStallDetectedAt = 0;
  }

  _armCaptureGuards(generation) {
    this._clearCaptureGuards();
    this._lastAudioFrameAt = performance.now();

    const tracks = this._stream?.getAudioTracks?.() || this._stream?.getTracks?.() || [];
    for (const track of tracks) {
      const handler = () => {
        if (generation !== this._generation || this._stopping || this._aborted) return;
        this._failLocalCapture(
          localCaptureFailure("El micrófono dejó de entregar audio durante el dictado.", "local-stt-track-ended"),
          generation,
        );
      };
      try {
        track.addEventListener?.("ended", handler);
        this._trackEndHandlers.push({ track, handler });
      } catch { /* watchdog still verifies readyState */ }
    }

    if (!this.continuous) {
      this._noSpeechTimer = setTimeout(() => {
        this._noSpeechTimer = null;
        if (generation !== this._generation || this._stopping || this._aborted || this._heardSpeech || this.state !== "listening") return;
        Promise.resolve(this.stop()).catch(() => undefined);
      }, ONE_SHOT_NO_SPEECH_TIMEOUT_MS);
    }

    this._captureWatchdogTimer = setInterval(() => {
      if (generation !== this._generation || this._stopping || this._aborted || this.state !== "listening") return;
      const now = performance.now();
      const liveTracks = this._stream?.getAudioTracks?.() || this._stream?.getTracks?.() || [];
      if (!liveTracks.length || liveTracks.every((track) => track?.readyState === "ended")) {
        this._failLocalCapture(
          localCaptureFailure("El micrófono dejó de estar disponible durante el dictado.", "local-stt-track-ended"),
          generation,
        );
        return;
      }

      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        // Los navegadores pueden pausar AudioContext al ocultar la pestaña. No
        // confundimos esa política con un micrófono roto; al volver damos un
        // período completo de gracia para que el flujo de audio se reactive.
        this._lastAudioFrameAt = now;
        this._audioResumeAttemptedAt = 0;
        this._audioStallDetectedAt = 0;
        return;
      }

      if (!this._lastAudioFrameAt || now - this._lastAudioFrameAt < AUDIO_FLOW_STALL_TIMEOUT_MS) return;
      if (!this._audioStallDetectedAt) {
        this._audioStallDetectedAt = now;
        const context = this._audioContext;
        if (context && context.state !== "running" && context.state !== "closed") {
          this._audioResumeAttemptedAt = now;
          try { Promise.resolve(context.resume?.()).catch(() => undefined); } catch { /* handled by grace timeout */ }
        }
        return;
      }
      if (now - this._audioStallDetectedAt < AUDIO_CONTEXT_RECOVERY_GRACE_MS) return;

      const context = this._audioContext;
      if (context && context.state !== "running" && context.state !== "closed" && !this._audioResumeAttemptedAt) {
        this._audioResumeAttemptedAt = now;
        try { Promise.resolve(context.resume?.()).catch(() => undefined); } catch { /* handled below */ }
        return;
      }

      this._failLocalCapture(
        localCaptureFailure("El flujo de audio del micrófono dejó de responder.", "local-stt-audio-stalled"),
        generation,
      );
    }, AUDIO_FLOW_WATCHDOG_INTERVAL_MS);
  }

  async _failLocalCapture(error, generation = this._generation) {
    if (generation !== this._generation || this._captureFailurePromise || this._stopping || this._aborted || ["idle", "error"].includes(this.state)) return;
    const previousState = this.state;
    this._generation += 1;
    this._stopping = true;
    this._aborted = true;

    const pending = (async () => {
      await this._cleanup();
      this._processing = false;
      this._stopping = false;
      this._transition("error", { operation: "capture" });
      try {
        this.onerror?.({
          error: "audio-capture",
          code: String(error?.code || "local-stt-audio-capture"),
          detail: error,
          lifecycle: this._failureLifecycle({ operation: "capture", previousState, attemptedState: "idle" }),
        });
      } catch { /* consumer observer only */ }
      try { this.onend?.({ previousState }); } catch { /* consumer observer only */ }
    })();

    this._captureFailurePromise = pending;
    try { await pending; } finally {
      if (this._captureFailurePromise === pending) this._captureFailurePromise = null;
    }
  }

  _onAudio(event) {
    if (this._stopping || this._aborted) return;
    this._lastAudioFrameAt = performance.now();
    this._audioResumeAttemptedAt = 0;
    this._audioStallDetectedAt = 0;
    const input = event.inputBuffer.getChannelData(0);

    let sum = 0;
    for (let index = 0; index < input.length; index += 1) sum += input[index] * input[index];
    const rms = Math.sqrt(sum / Math.max(1, input.length));
    const now = performance.now();
    if (rms >= SPEECH_RMS) {
      if (!this._heardSpeech) this._speechStartedAt = now;
      this._heardSpeech = true;
      this._lastSpeechAt = now;
    }

    const copy = new Float32Array(input.length);
    copy.set(input);
    this._chunks.push(copy);
    this._bufferedSamples += copy.length;

    // Jarvis puede quedar escuchando indefinidamente. Antes, cada frame silencioso
    // se guardaba para siempre y una pestaña abierta terminaba consumiendo toda la
    // memoria. Conservamos sólo un pequeño pre-roll hasta detectar voz y, durante
    // una frase, un límite duro apenas mayor que la duración máxima admitida.
    const sampleRate = this._audioContext?.sampleRate || event.inputBuffer?.sampleRate || 48000;
    this._trimBufferedAudio(this._heardSpeech ? MAX_CAPTURE_BUFFER_MS : PRE_ROLL_MS, sampleRate);

    if (this._heardSpeech && now - this._speechStartedAt >= MIN_UTTERANCE_MS && now - this._lastSpeechAt >= SILENCE_MS) {
      this.stop();
      return;
    }
    if (this._heardSpeech && now - this._speechStartedAt >= MAX_UTTERANCE_MS) this.stop();
  }

  _finishNative({ abort = false } = {}) {
    const recognition = this._nativeRecognition;
    if (!recognition) return undefined;

    const operation = abort ? "cancel" : "stop";
    const transitionState = abort ? "cancelling" : "stopping";
    const previousState = this.state;
    this._stopping = true;
    this._aborted = abort;
    this._transition(transitionState, { operation });

    if (abort) {
      // Invalidate the native callbacks before aborting. Some mobile engines
      // deliver a late error/end event after abort; it must not mutate a new
      // recognition session that has already started.
      this._generation += 1;
      this._nativeRecognition = null;
    }

    try {
      if (abort) recognition.abort?.();
      else recognition.stop?.();
      if (abort) {
        this._stopping = false;
        this._transition("idle", { operation });
      }
    } catch (error) {
      this._nativeRecognition = null;
      this._stopping = false;
      this._transition("error", { operation });
      try {
        this.onerror?.({
          error: normalizedNativeError(error),
          detail: error,
          lifecycle: this._failureLifecycle({ operation, previousState, attemptedState: "idle" }),
        });
      } catch { /* consumer observer only */ }
      try { this.onend?.(); } catch { /* consumer observer only */ }
    }
    return undefined;
  }

  async _finish({ abort = false } = {}) {
    if (this._nativeRecognition) return this._finishNative({ abort });
    if (this._finishPromise) {
      // Abort must still invalidate a transcribe already in flight. The worker may
      // finish its current inference, but its result can no longer reach this session.
      if (abort) {
        this._generation += 1;
        this._aborted = true;
        this._stopping = true;
        if (this.state !== "cancelling") this._transition("cancelling", { operation: "cancel" });
      }
      return this._finishPromise;
    }
    if (["idle", "error"].includes(this.state)) return undefined;

    const operation = abort ? "cancel" : "stop";
    const transitionState = abort ? "cancelling" : "stopping";
    this._generation += 1;
    this._stopping = true;
    this._aborted = abort;
    this._transition(transitionState, { operation });

    const pending = (async () => {
      const chunks = this._chunks;
      this._chunks = [];
      this._bufferedSamples = 0;
      const sampleRate = this._audioContext?.sampleRate || 48000;
      await this._cleanup();

      if (abort) {
        this._stopping = false;
        this._transition("idle", { operation });
        this.onerror?.({ error: "aborted", lifecycle: this._failureLifecycle({ operation, previousState: transitionState, attemptedState: "idle" }) });
        this.onend?.();
        return;
      }
      if (!this._heardSpeech || !chunks.length) {
        this._stopping = false;
        this._transition("idle", { operation });
        this.onerror?.({ error: "no-speech", lifecycle: this._failureLifecycle({ operation, previousState: transitionState, attemptedState: "idle" }) });
        this.onend?.();
        return;
      }

      this._processing = true;
      this._transition("processing", { operation: "transcribe" });
      try {
        const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const merged = new Float32Array(total);
        let offset = 0;
        for (const chunk of chunks) { merged.set(chunk, offset); offset += chunk.length; }
        const audio = resampleLinear(merged, sampleRate, TARGET_SAMPLE_RATE);
        const response = await workerRequest("transcribe", { audio: audio.buffer }, [audio.buffer]);
        const text = String(response?.text || "").trim();
        if (text && !this._aborted) this.onresult?.(makeResultEvent(text));
        else if (!text && !this._aborted) this.onerror?.({ error: "no-speech", lifecycle: this._failureLifecycle({ operation: "transcribe", previousState: "processing", attemptedState: "idle" }) });
      } catch (error) {
        if (!this._aborted) {
          const previousState = this.state;
          this._transition("error", { operation: "transcribe" });
          this.onerror?.({
            error: "local-stt",
            code: String(error?.code || "local-stt"),
            detail: error,
            lifecycle: this._failureLifecycle({ operation: "transcribe", previousState, attemptedState: "idle" }),
          });
        }
      } finally {
        this._processing = false;
        this._stopping = false;
        if (this.state !== "error") this._transition("idle", { operation: "finish" });
        this.onend?.();
      }
    })();

    this._finishPromise = pending;
    try { return await pending; } finally {
      if (this._finishPromise === pending) this._finishPromise = null;
    }
  }

  stop() {
    return this._finish({ abort: false });
  }

  abort() {
    return this._finish({ abort: true });
  }

  async _cleanup() {
    this._clearCaptureGuards();
    if (this._processor) {
      try { this._processor.disconnect(); } catch { /* noop */ }
      this._processor.onaudioprocess = null;
      this._processor = null;
    }
    if (this._source) {
      try { this._source.disconnect(); } catch { /* noop */ }
      this._source = null;
    }
    if (this._stream) {
      this._stream.getTracks().forEach((track) => { try { track.stop(); } catch { /* noop */ } });
      this._stream = null;
    }
    if (this._audioContext) {
      try { await this._audioContext.close(); } catch { /* noop */ }
      this._audioContext = null;
    }
  }
}
